import { useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/shared/auth/store';
import {
  WINDOW_ACCESS_REFUSAL,
  reportWouldForbid,
  windowGateModeOf,
  type ScreenAccess,
} from '@/shared/auth/screenAccess';

/**
 * ADR-0067 decision 6: the router-level window guard — ONE insertion point in `router.tsx`
 * around the whole route tree, not a wrapper per route.
 *
 * <p>The current URL is resolved to its route pattern through the checker (react-router's own
 * ranking over the window map's keys), then {@link ScreenAccess.mayOpenRoute} answers.
 * <ul>
 *   <li><b>No session</b> (login page, `RequireAuth` about to redirect): renders the children
 *       untouched.</li>
 *   <li><b>log</b> mode (tonight; also any session persisted by an older build): ALWAYS renders
 *       the page. A refused route is `console.info`d and POSTed to `/api/security/would-403`
 *       once per (session, route), fire-and-forget. A session without `allowedWindows`
 *       ("unknown") and a route the map does not know are never reported.</li>
 *   <li><b>enforce</b> mode: a refused route renders PB's refusal instead of the page.</li>
 * </ul>
 */
export function RequireWindow({
  access,
  children,
}: {
  access: ScreenAccess;
  children: ReactNode;
}) {
  const session = useAuthStore((s) => s.session);
  const { pathname, search } = useLocation();
  // Query-keyed mode routes (`?mode=license`) resolve to their own window, not the path's union.
  const pattern = session ? access.matchRoutePattern(pathname + search) : null;
  const verdict = session && pattern ? access.mayOpenRoute(session, pattern) : null;
  const refused = verdict !== null && !verdict.allowed;
  const mode = windowGateModeOf(session);

  useEffect(() => {
    if (!refused || mode !== 'log' || !session || !pattern || !verdict) return;
    reportWouldForbid(session, pattern, verdict);
    // The report is keyed by (session, route) inside reportWouldForbid; re-running is harmless.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refused, mode, session?.sessionId, pattern]);

  if (refused && mode === 'enforce') return <WindowAccessRefusal />;
  return <>{children}</>;
}

/** PB's "You do not have security access to this window." (`f_phfc_open_window_std.srf`). */
export function WindowAccessRefusal() {
  return (
    <div role="alert" className="m-6 rounded border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
      {WINDOW_ACCESS_REFUSAL}
    </div>
  );
}
